import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { Modal } from "@components/ui/Modal";
import { QueryState } from "@components/ui/QueryState";
import {
  useGetWellboresQuery,
  useCreateWellboreMutation,
  useUpdateWellboreMutation,
} from "@api/mApi";
import { isGlobalAdmin } from "@api/brokerAuth";
import type { WellboreOut, WellboreCreate, WellborePatch } from "@api/types";

/** Whether a 403 (admins-only) is buried in an RTK Query error. */
function isForbidden(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403
  );
}

/** Format a nullable depth pair "MD / TVD" (— for a missing side). */
function fmtPair(md: number | null, tvd: number | null): string {
  if (md == null && tvd == null) return "—";
  return `${md ?? "—"} / ${tvd ?? "—"}`;
}

/**
 * "Pozos perforados / Wellbores" section of the Ficha del pozo. Lists a well's
 * wellbores — the Original Hole (OH) + any sidetracks — with wellbore_no, name,
 * type, parent, KOP (MD/TVD) and authorized (MD/TVD). A well-writer / admin gets
 * a "+ Agregar sidetrack" action and a per-row "Editar", both opening the shared
 * Modal wired to the M create/update mutations. The list re-reads on save (the
 * Wellbore tag is scoped to this well).
 */
export function WellboresSection({ wellId }: { wellId: string }) {
  const { t } = useLocale();
  const admin = isGlobalAdmin();
  const { data: wellbores = [], isLoading, isError } = useGetWellboresQuery(wellId);

  const [editing, setEditing] = useState<WellboreOut | null>(null);
  const [adding, setAdding] = useState(false);

  const typeLabel = (wb: WellboreOut) =>
    wb.is_sidetrack ? t("well.wellbores.typeSidetrack") : t("well.wellbores.typeOh");

  const parentNo = (wb: WellboreOut): string => {
    if (wb.parent_wellbore_id == null) return "—";
    const parent = wellbores.find((p) => p.id === wb.parent_wellbore_id);
    return parent?.wellbore_no ?? parent?.wellbore_name ?? "—";
  };

  return (
    <div className="card" data-owner="m" style={{ marginBottom: 14 }}>
      <div className="panel-head-row">
        <div>
          <h3 style={{ margin: 0 }}>{t("well.wellbores.title")}</h3>
          <p className="subtitle" style={{ margin: "2px 0 0", color: "var(--dim)" }}>
            {t("well.wellbores.subtitle")}
          </p>
        </div>
        {admin ? (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            {t("well.wellbores.addSidetrack")}
          </button>
        ) : null}
      </div>

      <QueryState isLoading={isLoading} isError={isError} isEmpty={wellbores.length === 0}>
        <div className="empty-state">{t("well.wellbores.empty")}</div>
      </QueryState>

      {wellbores.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("well.wellbores.col.no")}</th>
                <th>{t("well.wellbores.col.name")}</th>
                <th>{t("well.wellbores.col.type")}</th>
                <th>{t("well.wellbores.col.parent")}</th>
                <th className="num">{t("well.wellbores.col.kop")}</th>
                <th className="num">{t("well.wellbores.col.authorized")}</th>
                {admin ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {wellbores.map((wb) => (
                <tr key={wb.id}>
                  <td className="mono">{wb.wellbore_no ?? "—"}</td>
                  <td>{wb.wellbore_name ?? "—"}</td>
                  <td>{typeLabel(wb)}</td>
                  <td className="mono">{parentNo(wb)}</td>
                  <td className="num mono">{fmtPair(wb.ko_md, wb.ko_tvd)}</td>
                  <td className="num mono">{fmtPair(wb.authorized_md, wb.authorized_tvd)}</td>
                  {admin ? (
                    <td>
                      <button type="button" className="btn" onClick={() => setEditing(wb)}>
                        {t("well.wellbores.edit")}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {adding ? (
        <WellboreFormModal
          wellId={wellId}
          wellbores={wellbores}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {editing ? (
        <WellboreFormModal
          wellId={wellId}
          wellbores={wellbores}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

/** Coerce a numeric text input → number | null (blank = null). */
function parseNum(raw: string): number | null {
  const s = raw.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * The add/edit form for a wellbore, in the shared Modal. On add it defaults to a
 * sidetrack (the primary OH is auto-created with the well); the fields are
 * nombre, ¿sidetrack?, pozo padre (a select over existing wellbores), KOP MD/TVD,
 * fecha KOP, MD/TVD autorizado, motivo. A sidetrack requires a parent — Guardar
 * stays disabled until one is chosen.
 */
function WellboreFormModal({
  wellId,
  wellbores,
  existing,
  onClose,
}: {
  wellId: string;
  wellbores: WellboreOut[];
  existing?: WellboreOut;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const [createWellbore, createState] = useCreateWellboreMutation();
  const [updateWellbore, updateState] = useUpdateWellboreMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  // On add default to a sidetrack (OH is auto-created with the well).
  const [name, setName] = useState(existing?.wellbore_name ?? "");
  const [isSidetrack, setIsSidetrack] = useState(existing ? existing.is_sidetrack : true);
  const [parentId, setParentId] = useState(existing?.parent_wellbore_id ?? "");
  const [koMd, setKoMd] = useState(existing?.ko_md == null ? "" : String(existing.ko_md));
  const [koTvd, setKoTvd] = useState(existing?.ko_tvd == null ? "" : String(existing.ko_tvd));
  const [koDate, setKoDate] = useState(existing?.ko_date ? existing.ko_date.slice(0, 10) : "");
  const [authMd, setAuthMd] = useState(
    existing?.authorized_md == null ? "" : String(existing.authorized_md),
  );
  const [authTvd, setAuthTvd] = useState(
    existing?.authorized_tvd == null ? "" : String(existing.authorized_tvd),
  );
  const [reason, setReason] = useState(existing?.reason ?? "");

  // Parent candidates: every OTHER wellbore of this well (can't parent itself).
  const parentOptions = wellbores.filter((w) => w.id !== existing?.id);

  // A sidetrack needs a parent; without one, Guardar stays disabled.
  const canConfirm = !isSidetrack || parentId !== "";

  const save = async () => {
    const koDateIso = koDate ? koDate : null;
    if (existing) {
      const body: WellborePatch = {
        wellbore_name: name || null,
        is_sidetrack: isSidetrack,
        parent_wellbore_id: isSidetrack ? parentId || null : null,
        ko_md: parseNum(koMd),
        ko_tvd: parseNum(koTvd),
        ko_date: koDateIso,
        authorized_md: parseNum(authMd),
        authorized_tvd: parseNum(authTvd),
        reason: reason || null,
      };
      const res = await updateWellbore({ wellId, wellboreId: existing.id, body });
      if (!("error" in res)) onClose();
      return;
    }
    const body: WellboreCreate = {
      wellbore_name: name || null,
      is_sidetrack: isSidetrack,
      parent_wellbore_id: isSidetrack ? parentId || null : null,
      ko_md: parseNum(koMd),
      ko_tvd: parseNum(koTvd),
      ko_date: koDateIso,
      authorized_md: parseNum(authMd),
      authorized_tvd: parseNum(authTvd),
      reason: reason || null,
    };
    const res = await createWellbore({ wellId, body });
    if (!("error" in res)) onClose();
  };

  const forbidden = isForbidden(error);

  return (
    <Modal
      open
      title={existing ? t("well.wellbores.editTitle") : t("well.wellbores.addTitle")}
      onClose={onClose}
      onConfirm={() => void save()}
      confirmLabel={busy ? t("well.wellbores.saving") : t("well.wellbores.save")}
      cancelLabel={t("well.wellbores.cancel")}
      busy={busy}
      canConfirm={canConfirm}
      footerNote={
        error ? (
          <span className="pick-error">
            {forbidden ? t("well.wellbores.forbidden") : t("well.wellbores.error")}
          </span>
        ) : !canConfirm ? (
          <span className="pick-error">{t("well.wellbores.parentRequired")}</span>
        ) : null
      }
    >
      <div className="loc-form" style={{ display: "grid", gap: 10 }}>
        <label className="field">
          <span className="kv-k">{t("well.wellbores.form.name")}</span>
          <input
            className="pick-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t("well.wellbores.form.name")}
          />
        </label>

        <label className="field" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            className="brand-check"
            type="checkbox"
            checked={isSidetrack}
            onChange={(e) => setIsSidetrack(e.target.checked)}
            aria-label={t("well.wellbores.form.isSidetrack")}
          />
          <span className="kv-k">{t("well.wellbores.form.isSidetrack")}</span>
        </label>

        {isSidetrack ? (
          <label className="field">
            <span className="kv-k">{t("well.wellbores.form.parent")}</span>
            <select
              className="pick-input"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              aria-label={t("well.wellbores.form.parent")}
            >
              <option value="">{t("well.wellbores.form.parentNone")}</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.wellbore_no ?? p.wellbore_name ?? p.id}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label className="field">
            <span className="kv-k">{t("well.wellbores.form.koMd")}</span>
            <input
              className="pick-input mono"
              type="number"
              step="any"
              inputMode="decimal"
              value={koMd}
              onChange={(e) => setKoMd(e.target.value)}
              aria-label={t("well.wellbores.form.koMd")}
            />
          </label>
          <label className="field">
            <span className="kv-k">{t("well.wellbores.form.koTvd")}</span>
            <input
              className="pick-input mono"
              type="number"
              step="any"
              inputMode="decimal"
              value={koTvd}
              onChange={(e) => setKoTvd(e.target.value)}
              aria-label={t("well.wellbores.form.koTvd")}
            />
          </label>
        </div>

        <label className="field">
          <span className="kv-k">{t("well.wellbores.form.koDate")}</span>
          <input
            className="pick-input"
            type="date"
            value={koDate}
            onChange={(e) => setKoDate(e.target.value)}
            aria-label={t("well.wellbores.form.koDate")}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label className="field">
            <span className="kv-k">{t("well.wellbores.form.authorizedMd")}</span>
            <input
              className="pick-input mono"
              type="number"
              step="any"
              inputMode="decimal"
              value={authMd}
              onChange={(e) => setAuthMd(e.target.value)}
              aria-label={t("well.wellbores.form.authorizedMd")}
            />
          </label>
          <label className="field">
            <span className="kv-k">{t("well.wellbores.form.authorizedTvd")}</span>
            <input
              className="pick-input mono"
              type="number"
              step="any"
              inputMode="decimal"
              value={authTvd}
              onChange={(e) => setAuthTvd(e.target.value)}
              aria-label={t("well.wellbores.form.authorizedTvd")}
            />
          </label>
        </div>

        <label className="field">
          <span className="kv-k">{t("well.wellbores.form.reason")}</span>
          <input
            className="pick-input"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={t("well.wellbores.form.reason")}
          />
        </label>
      </div>
    </Modal>
  );
}
